import React, {useMemo, useCallback, memo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Button from '../../../../incase-domain/components/button/Button';
import {DateTimeFormat, formatDateTime} from '../../../../utils/dateTimeUtils';
import {useMarkConversationAsSeen} from '../queries';
import {IConversation} from '../../../../incase-domain/message';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';

interface IConversationsListProps {
    setSearchString: (searchString: string) => void;
    allConversations: IConversation[];
    showHeader?: boolean;
    infiniteScroll?: {
        fetchNextPage: () => void;
        hasNextPage: boolean;
        scrollableTargetId: string;
    };
}

function ConversationsList({allConversations, setSearchString, showHeader = true, infiniteScroll}: IConversationsListProps): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {conversationId} = useParams();

    const loggedInUser = useSelector(loggedInUserSelector);

    const removeUserFromParticipants = useCallback(
        (participants: {userId: string; email?: string}[]): string => {
            return participants
                .filter((p) => p.userId !== loggedInUser?._id)
                .map((p) => p.email)
                .join(', ');
        },
        [loggedInUser?._id]
    );

    const markConversationAsSeenAction = useMarkConversationAsSeen();

    const handleConversationClick = useCallback(
        (conversationId: string) => {
            setSearchString('');
            navigate(`/messages/${conversationId}`);
            markConversationAsSeenAction.mutate(conversationId);
        },
        [setSearchString, navigate, markConversationAsSeenAction]
    );

    const conversationButtons = useMemo(() => {
        return allConversations.map((conversation) => (
            <Button
                fullWidth={!!infiniteScroll}
                disabled={infiniteScroll ? !conversation.connected : false}
                data-cy="messagingConversation"
                color="primary"
                key={conversation._id}
                variant={conversation._id === conversationId ? 'soft' : 'ghost'}
                size="lg"
                align="left"
                leading="solar:user-rounded-linear"
                trailing={infiniteScroll && conversation.numberOfUnseen ? 'ci:dot-05-xl' : ''}
                onClick={() => handleConversationClick(conversation._id as string)}
            >
                <div className={`c-conversations__item c-conversations__item--${conversation.numberOfUnseen ? 'unread' : ''}`}>
                    <div>
                        {removeUserFromParticipants(conversation.participantsDetailed)}
                        {conversation.numberOfUnseen !== 0 && <span className="badge">{conversation.numberOfUnseen}</span>}
                    </div>
                    {conversation.lastMessage && (
                        <div className="c-conversations__item_bottom">
                            <div className="c-conversations__item_bottom_lastMessage">{conversation.lastMessage?.text}</div>
                            &bull;
                            <span className="c-conversations__item_bottom_time">
                                {formatDateTime(conversation.lastModified, DateTimeFormat.DATE_OR_TIME)}
                            </span>
                        </div>
                    )}
                </div>
            </Button>
        ));
    }, [allConversations, conversationId, infiniteScroll, removeUserFromParticipants, handleConversationClick]);

    return (
        <>
            {showHeader && <div className="c-conversations__groupHeader">{t('messaging.conversations')}</div>}
            {infiniteScroll ? (
                <InfiniteScroll
                    style={{position: 'relative', overflow: 'visible'}}
                    dataLength={allConversations.length}
                    next={() => infiniteScroll.fetchNextPage()}
                    hasMore={infiniteScroll.hasNextPage || false}
                    loader={infiniteScroll.hasNextPage && <h4>Loading...</h4>}
                    scrollableTarget={infiniteScroll.scrollableTargetId}
                >
                    {conversationButtons}
                </InfiniteScroll>
            ) : (
                conversationButtons
            )}
        </>
    );
}

export default memo(ConversationsList);
